import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Lock, X } from 'lucide-react';
import { RenderedHtml } from 'modules/editor';
import { cn } from 'lib/utils';
import { LISTENING_PARTS, isAnswered, listeningParts, trackGroups } from '../../constants';
import { ExamAnswerArea, PROMPT_OWNED_TYPES } from './ExamAnswerArea';
import { ListeningAudio } from './ListeningAudio';
import { loadSectionPart, saveSectionPart } from './sectionPart';
import { useExamFlags } from './ExamFlags';
import type { CandidateQuestion, CandidateSection } from '../../types';

interface Props {
  attemptId: string;
  section: CandidateSection;
  answers: Map<string, unknown>;
  onAnswer: (questionId: string, value: unknown) => void;
  /** Open this question (from the review grid); bump `seq` to repeat a jump to the same one. */
  jump?: { questionId: string; seq: number } | null;
}

interface NumberedQuestion {
  question: CandidateQuestion;
  number: number;
  part: number;
}

/**
 * IELTS Listening: the recording strip, one part's questions at a time, part
 * tabs, and a question navigator card (a bottom sheet on phones), like the
 * navigator of a regular test.
 */
export function ListeningSectionView({ attemptId, section, answers, onAnswer, jump }: Props) {
  const { t } = useTranslation();
  const { flagged, toggle: toggleFlag } = useExamFlags();
  const stimulusTracks = section.stimulus?.tracks;
  const tracks = useMemo(() => stimulusTracks ?? [], [stimulusTracks]);
  const trackCount = tracks.length;
  const perPart = section.stimulus?.questionsPerPart;
  const singlePlay = section.stimulus?.playback !== 'PRACTICE';

  // Clip groups drawn by the teacher's dividers; two or more make the parts.
  const groups = useMemo(() => trackGroups(tracks), [tracks]);
  const groupCount = groups.length ? groups[groups.length - 1] + 1 : 0;
  const grouped = groupCount >= 2;

  const parts = useMemo<NumberedQuestion[][]>(() => {
    let number = 0;
    return listeningParts(section.questions, trackCount, perPart, groupCount).map((qs, part) =>
      qs.map((question) => ({ question, number: ++number, part })),
    );
  }, [section.questions, trackCount, perPart, groupCount]);
  const flat = useMemo(() => parts.flat(), [parts]);

  // Which part a playing clip belongs to (null: the questions do not follow).
  const partOf = (index: number): number | null => {
    if (grouped) return Math.min(groups[index] ?? 0, parts.length - 1);
    if (!perPart && trackCount >= 2 && trackCount <= LISTENING_PARTS) return Math.min(index, parts.length - 1);
    return null;
  };

  // Single-play with dividers: a part stays locked until its audio begins.
  const startedPart = tracks.reduce((max, track, i) => (track.started ? Math.max(max, partOf(i) ?? 0) : max), 0);
  const [reachedPart, setReachedPart] = useState(startedPart);
  const isLocked = (part: number) => singlePlay && grouped && part > reachedPart;

  // A reload opens the part the candidate was on (never a still-locked one).
  const [activePart, setActivePart] = useState(() => {
    const saved = loadSectionPart(attemptId, section.id, parts.length);
    return singlePlay && grouped ? Math.min(saved, startedPart) : saved;
  });
  const [activeNumber, setActiveNumber] = useState(() => parts[activePart]?.[0]?.number ?? 1);
  useEffect(() => {
    saveSectionPart(attemptId, section.id, activePart);
  }, [attemptId, section.id, activePart]);
  const [pendingFocus, setPendingFocus] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const partQuestions = parts[activePart] ?? [];
  const answeredIn = (items: NumberedQuestion[]) => items.filter((i) => isAnswered(answers.get(i.question.id))).length;
  const answeredTotal = answeredIn(flat);

  // Open a part at the top, as a new page.
  const openPart = (part: number) => {
    const first = parts[part]?.[0];
    if (!first) return;
    setActivePart(part);
    setActiveNumber(first.number);
    setMapOpen(false);
    scrollRef.current?.scrollTo({ top: 0 });
  };

  // Jump to one question: open its part, then scroll to it and focus its first control.
  const goTo = (number: number) => {
    const target = flat.find((i) => i.number === number);
    if (!target || isLocked(target.part)) return;
    setActiveNumber(number);
    setActivePart(target.part);
    setPendingFocus(target.question.id);
    setMapOpen(false);
  };

  useEffect(() => {
    if (!pendingFocus) return;
    const el = document.getElementById(`exam-q-${pendingFocus}`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    el?.querySelector<HTMLElement>('input, textarea, select, button, [role="radio"]')?.focus({ preventScroll: true });
    setPendingFocus(null);
  }, [pendingFocus, activePart]);

  // The review grid opens a question: its part, then the question.
  useEffect(() => {
    const target = jump ? flat.find((i) => i.question.id === jump.questionId) : undefined;
    if (target) goTo(target.number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump?.seq]);

  // The questions follow the recording. On a reload the first report rejoins
  // the part that is playing; after that, playback crossing into the next part
  // opens it, unless the candidate has navigated elsewhere to review.
  const livePartRef = useRef<number | null>(null);
  const onTrackChange = (index: number) => {
    const part = partOf(index);
    if (part === null) return;
    const previous = livePartRef.current;
    livePartRef.current = part;
    setReachedPart((reached) => Math.max(reached, part));
    const follows = previous === null ? part !== activePart : part !== previous && activePart === previous;
    if (follows) openPart(part);
  };

  const from = partQuestions[0]?.number ?? 0;
  const to = partQuestions[partQuestions.length - 1]?.number ?? 0;

  const navigator = (
    <QuestionNavigator
      parts={parts}
      activePart={activePart}
      activeNumber={activeNumber}
      answeredTotal={answeredTotal}
      total={flat.length}
      isAnswered={(id) => isAnswered(answers.get(id))}
      isFlagged={(id) => flagged.has(id)}
      isLocked={isLocked}
      onOpenPart={openPart}
      onGoTo={goTo}
    />
  );

  const activeIndex = flat.findIndex((i) => i.number === activeNumber);
  const stepTo = (delta: number) => {
    const target = flat[activeIndex + delta];
    if (target) goTo(target.number);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <ListeningAudio attemptId={attemptId} section={section} onTrackChange={onTrackChange} />

      <main ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto py-5 sm:py-7">
        <section aria-labelledby="listening-part-title" className="mx-auto w-full max-w-[880px] px-4 sm:px-6">
          <div className="rounded-xl border-[1.5px] border-[#10182B] bg-[#FBFCFE] px-4 py-3">
            <h2 id="listening-part-title" className="font-display text-base font-bold text-[#10182B]">
              {partQuestions.length ? t('exam.listening.questions_range', { from, to }) : t('exam.empty_section')}
            </h2>
            <p className="mt-0.5 text-sm italic text-[#364055]">{t('exam.listening.part_hint')}</p>
          </div>

          <ol>
            {partQuestions.map(({ question, number }) => (
              <li
                key={question.id}
                id={`exam-q-${question.id}`}
                onFocusCapture={() => setActiveNumber(number)}
                className="scroll-mt-6 border-b border-[#EEF1F8] py-4"
              >
                <div className="flex gap-3.5">
                  <span
                    className={cn(
                      'flex h-[30px] min-w-[30px] shrink-0 items-center justify-center rounded-lg border-[1.5px] px-1.5 text-sm font-extrabold',
                      activeNumber === number ? 'border-[#B42318] bg-[#FDECEC] text-[#B42318]' : 'border-[#10182B] text-[#10182B]',
                    )}
                    aria-label={t('exam.nav.question_of', { n: number, total: flat.length })}
                  >
                    {number}
                  </span>
                  <div className="min-w-0 flex-1 space-y-3">
                    {!PROMPT_OWNED_TYPES.has(question.type) && (
                      <div className="text-[15.5px] font-semibold leading-snug text-[#10182B]">
                        <RenderedHtml html={question.prompt || ''} />
                      </div>
                    )}
                    <ExamAnswerArea question={question} value={answers.get(question.id)} onChange={(v) => onAnswer(question.id, v)} />
                  </div>
                  <FlagButton flagged={flagged.has(question.id)} onToggle={() => toggleFlag(question.id)} />
                </div>
              </li>
            ))}
          </ol>

          {activePart < parts.length - 1 && (
            <button
              type="button"
              onClick={() => openPart(activePart + 1)}
              disabled={isLocked(activePart + 1)}
              title={isLocked(activePart + 1) ? t('exam.listening.part_locked') : undefined}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#CBD2E3] py-3 text-sm font-semibold text-[#364055] hover:bg-[#F4F6FB] disabled:text-[#94A3B8] disabled:hover:bg-transparent"
            >
              {isLocked(activePart + 1) && <Lock className="h-4 w-4" aria-hidden />}
              {isLocked(activePart + 1) ? t('exam.listening.part_locked') : t('exam.listening.go_to_part', { n: activePart + 2 })}
              {!isLocked(activePart + 1) && <ChevronRight className="h-4 w-4" aria-hidden />}
            </button>
          )}
        </section>
      </main>

      {/* Desktop: the parts and the open part's question squares, like the computer-delivered paper. */}
      <footer
        aria-label={t('exam.nav.map')}
        className="hidden h-24 shrink-0 items-center gap-3.5 border-t border-[#E6E9F2] bg-white px-6 shadow-[0_-8px_24px_-18px_rgba(16,24,43,0.4)] lg:flex"
      >
        <nav className="flex gap-2" aria-label={t('exam.parts')}>
          {parts.map((items, p) => {
            const done = answeredIn(items);
            const locked = isLocked(p);
            const open = p === activePart;
            return (
              <button
                key={p}
                type="button"
                onClick={() => !locked && openPart(p)}
                disabled={locked}
                title={locked ? t('exam.listening.part_locked') : undefined}
                aria-current={open ? 'page' : undefined}
                className={cn(
                  'flex min-w-[112px] flex-col gap-1.5 rounded-xl border px-3.5 py-2 text-left focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]',
                  open ? 'border-[#10182B] bg-white shadow-[0_0_0_2px_#EEF1F8]' : 'border-[#E6E9F2] bg-[#FBFCFE]',
                  locked && 'opacity-60',
                )}
              >
                <b className="flex items-center gap-1.5 text-[13px] text-[#10182B]">
                  {locked && <Lock className="h-3 w-3" aria-hidden />}
                  {t('exam.listening.part', { n: p + 1 })}
                </b>
                <span className="text-xs text-[#646D84]">
                  {locked ? t('exam.listening.part_locked') : `${done}/${items.length}`}
                  {!locked && done === items.length ? ' ✓' : ''}
                </span>
              </button>
            );
          })}
        </nav>
        <div className="flex gap-[5px]" role="group" aria-label={t('exam.listening.part', { n: activePart + 1 })}>
          {partQuestions.map((item) => (
            <PaletteSquare
              key={item.question.id}
              number={item.number}
              answered={isAnswered(answers.get(item.question.id))}
              flagged={flagged.has(item.question.id)}
              current={item.number === activeNumber}
              onClick={() => goTo(item.number)}
            />
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-[#646D84]">{answeredTotal}/{flat.length}</span>
          <NavArrow direction="prev" disabled={activeIndex <= 0} onClick={() => stepTo(-1)} />
          <NavArrow direction="next" disabled={activeIndex < 0 || activeIndex >= flat.length - 1 || isLocked(flat[activeIndex + 1]?.part ?? 0)} onClick={() => stepTo(1)} />
        </div>
      </footer>

      {/* Narrower screens: the question map in a sheet, and moving between parts. */}
      <footer className="flex min-h-[68px] shrink-0 items-center gap-2.5 border-t border-[#E6E9F2] bg-white px-4 pb-[env(safe-area-inset-bottom)] sm:px-6 lg:hidden">
        <button
          type="button"
          onClick={() => setMapOpen(true)}
          aria-haspopup="dialog"
          className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[10px] bg-[#10182B] px-3 text-[13.5px] font-semibold text-white focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] sm:max-w-xs"
        >
          <span className="truncate">{t('exam.nav.map')}</span>
          <span className="shrink-0 font-medium text-slate-400">· {answeredTotal}/{flat.length}</span>
          <ChevronUp className="h-4 w-4 shrink-0 max-[359px]:hidden" aria-hidden />
        </button>
        <div className="flex-1 max-sm:hidden" />
        <PartButton direction="prev" disabled={activePart === 0} onClick={() => openPart(activePart - 1)} />
        <PartButton direction="next" disabled={activePart >= parts.length - 1 || isLocked(activePart + 1)} onClick={() => openPart(activePart + 1)} />
      </footer>

      {mapOpen && (
        <div className="fixed inset-0 z-40 flex items-end bg-black/40 lg:hidden" onClick={() => setMapOpen(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('exam.nav.map')}
            className="max-h-[80dvh] w-full overflow-y-auto rounded-t-2xl bg-white p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:mx-auto sm:mb-6 sm:max-w-lg sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-[#CBD2E3]" aria-hidden />
            <div className="mb-1 flex justify-end">
              <button
                type="button"
                onClick={() => setMapOpen(false)}
                aria-label={t('exam.nav.close')}
                className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            {navigator}
            <p className="mt-3 text-xs text-[#646D84]">{t('exam.listening.cond_next')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/** Parts with answered counts and a number grid, like a regular test's navigator. */
function QuestionNavigator({
  parts,
  activePart,
  activeNumber,
  answeredTotal,
  total,
  isAnswered: answered,
  isFlagged,
  isLocked,
  onOpenPart,
  onGoTo,
}: {
  parts: NumberedQuestion[][];
  activePart: number;
  activeNumber: number;
  answeredTotal: number;
  total: number;
  isAnswered: (questionId: string) => boolean;
  isFlagged: (questionId: string) => boolean;
  isLocked: (part: number) => boolean;
  onOpenPart: (part: number) => void;
  onGoTo: (number: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-500">{t('exam.nav.map')}</span>
        <span className="font-mono text-xs font-semibold text-gray-700">
          {answeredTotal}/{total}
        </span>
      </div>
      <div className="space-y-2">
        {parts.map((items, p) => {
          const done = items.filter((i) => answered(i.question.id)).length;
          const open = p === activePart;
          const locked = isLocked(p);
          return (
            <div key={p} className={cn('rounded-xl border', open ? 'border-gray-300 bg-gray-50/60' : 'border-gray-200')}>
              <button
                type="button"
                onClick={() => !locked && onOpenPart(p)}
                disabled={locked}
                title={locked ? t('exam.listening.part_locked') : undefined}
                aria-expanded={open}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left',
                  locked ? 'text-gray-400' : 'hover:bg-gray-50',
                )}
              >
                <span className={cn('text-[13px] font-semibold', locked ? 'text-gray-400' : 'text-gray-900')}>
                  {t('exam.listening.part', { n: p + 1 })}
                  <span className={cn('ml-1.5 font-normal', locked ? 'text-gray-400' : 'text-gray-500')}>
                    {items[0]?.number}–{items[items.length - 1]?.number}
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  {locked ? (
                    <Lock className="h-3.5 w-3.5 text-gray-400" aria-hidden />
                  ) : (
                    <span className={cn('font-mono text-xs', done === items.length ? 'text-green-700' : 'text-gray-500')}>
                      {done}/{items.length}
                    </span>
                  )}
                  <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', !open && '-rotate-90')} aria-hidden />
                </span>
              </button>
              <div className="mx-3 h-1 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full bg-primary transition-[width]" style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
              </div>
              {open ? (
                <div className="grid grid-cols-5 gap-1.5 p-3">
                  {items.map((item) => (
                    <QuestionCell
                      key={item.question.id}
                      number={item.number}
                      answered={answered(item.question.id)}
                      flagged={isFlagged(item.question.id)}
                      current={item.number === activeNumber}
                      onClick={() => onGoTo(item.number)}
                    />
                  ))}
                </div>
              ) : (
                <div className="h-2" />
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border-2 border-gray-900 bg-white" /> {t('exam.nav.current')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-gray-900 bg-gray-900" /> {t('exam.nav.answered')}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-gray-200 bg-white" /> {t('exam.nav.unanswered')}
        </span>
      </div>
    </div>
  );
}

function QuestionCell({ number, answered, flagged, current, onClick }: { number: number; answered: boolean; flagged: boolean; current: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={current ? 'step' : undefined}
      aria-label={`${t('exam.nav.question_status', { n: number, status: answered ? t('exam.nav.answered') : t('exam.nav.unanswered') })}${flagged ? `, ${t('exam.flag.flagged')}` : ''}`}
      className={cn(
        'relative flex h-11 items-center justify-center rounded-xl border-[1.5px] text-[15px] font-bold transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]',
        current ? 'border-[2.5px] border-[#B42318]' : answered ? '' : 'border-[#94A3B8]',
        answered ? 'bg-[#10182B] text-white' : 'bg-white text-[#10182B] hover:bg-[#F4F6FB]',
        current && answered && 'border-[#B42318]',
        flagged && FLAG_CORNER,
      )}
    >
      {number}
    </button>
  );
}

/** The red corner that marks a flagged question on a palette square. */
const FLAG_CORNER =
  "after:absolute after:right-[-1px] after:top-[-1px] after:h-0 after:w-0 after:rounded-tr-[7px] after:border-l-[9px] after:border-t-[9px] after:border-l-transparent after:border-t-[#B42318] after:content-['']";

function FlagButton({ flagged, onToggle }: { flagged: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={flagged}
      className={cn(
        'inline-flex h-[30px] shrink-0 items-center gap-1.5 self-start rounded-lg border px-2.5 text-xs font-semibold max-sm:h-11 max-sm:w-11 max-sm:justify-center max-sm:px-0 [@media(pointer:coarse)]:h-11 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]',
        flagged ? 'border-[#F5B5B0] bg-[#FDECEC] text-[#B42318]' : 'border-[#E6E9F2] bg-white text-[#4B5468] hover:bg-[#F4F6FB]',
      )}
    >
      <span aria-hidden>⚑</span>
      <span className="max-sm:sr-only">{t(flagged ? 'exam.flag.flagged' : 'exam.flag.flag')}</span>
    </button>
  );
}

/** One small square of the desktop palette. */
function PaletteSquare({ number, answered, flagged, current, onClick }: { number: number; answered: boolean; flagged: boolean; current: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={current ? 'step' : undefined}
      aria-label={`${t('exam.nav.question_status', { n: number, status: answered ? t('exam.nav.answered') : t('exam.nav.unanswered') })}${flagged ? `, ${t('exam.flag.flagged')}` : ''}`}
      className={cn(
        'relative flex h-7 w-7 items-center justify-center rounded-lg border-[1.5px] p-0 text-[12.5px] font-bold focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]',
        answered ? 'border-[#10182B] bg-[#10182B] text-white' : 'border-[#94A3B8] bg-white text-[#10182B]',
        current && 'border-[2.5px] border-[#B42318]',
        flagged && FLAG_CORNER,
      )}
    >
      {number}
    </button>
  );
}

function NavArrow({ direction, disabled, onClick }: { direction: 'prev' | 'next'; disabled: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  const next = direction === 'next';
  const label = next ? t('exam.nav.next') : t('exam.nav.previous');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-11 w-11 items-center justify-center rounded-xl border-[1.5px] border-[#10182B] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B] disabled:opacity-40',
        next ? 'bg-[#10182B] text-white' : 'bg-white text-[#10182B]',
      )}
    >
      {next ? <ChevronRight className="h-5 w-5" aria-hidden /> : <ChevronLeft className="h-5 w-5" aria-hidden />}
    </button>
  );
}

function PartButton({ direction, disabled, onClick }: { direction: 'prev' | 'next'; disabled: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  const next = direction === 'next';
  const label = next ? t('exam.listening.next_part') : t('exam.listening.prev_part');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-11 shrink-0 items-center justify-center gap-2 rounded-full border text-[13px] font-medium disabled:opacity-40 max-sm:w-11 sm:h-9 sm:rounded-lg sm:px-3 [@media(pointer:coarse)]:h-11',
        next ? 'border-[#0A1628] bg-[#0A1628] text-white hover:bg-[#16243d]' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
      )}
    >
      {!next && <ChevronLeft className="h-4 w-4" aria-hidden />}
      <span className="max-sm:sr-only">{label}</span>
      {next && <ChevronRight className="h-4 w-4" aria-hidden />}
    </button>
  );
}

export default ListeningSectionView;
